import { useEffect } from 'react';
import {
  Box, FormControl, InputLabel, MenuItem, Select,
  Typography, Button, CircularProgress, Alert, TextField,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getAcademicYearsDirect } from '@timetable/api';
import { useAuthStore } from '@/store/authStore';
import type { WizardState, WizardAction } from '../ImportWizard';

interface Step3Props {
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
  onNext: (patch?: Record<string, unknown>) => Promise<void>;
  onBack?: () => void;
}

function tomorrow(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

export function Step3AcademicYear({ state, dispatch, onNext, onBack }: Step3Props) {
  const { schoolContext } = useAuthStore();

  const { data: academicYears = [], isLoading } = useQuery({
    queryKey: ['academic-years', schoolContext],
    queryFn: () => getAcademicYearsDirect({ school_id: schoolContext! }),
    enabled: !!schoolContext,
  });

  // Auto-select if only one year available
  useEffect(() => {
    if (academicYears.length === 1 && !state.selectedAcademicYear) {
      const ay = academicYears[0];
      dispatch({ type: 'SET_ACADEMIC_YEAR', payload: ay.name });
      dispatch({ type: 'SET_DATE_RANGE', payload: { validFrom: tomorrow(), validTo: ay.end_date } });
    }
  }, [academicYears, state.selectedAcademicYear, dispatch]);

  const handleYearChange = (name: string) => {
    const ay = academicYears.find((a) => a.name === name);
    dispatch({ type: 'SET_ACADEMIC_YEAR', payload: name });
    dispatch({ type: 'SET_DATE_RANGE', payload: {
      validFrom: tomorrow(),
      validTo: ay?.end_date ?? '',
    }});
  };

  const today = new Date().toISOString().slice(0, 10);
  const validFromError = state.validFrom && state.validFrom <= today
    ? 'Effective date must be in the future'
    : null;

  const canAdvance = !!state.selectedAcademicYear && !!state.validFrom && !validFromError;

  const handleNext = async () => {
    await onNext({
      academic_year: state.selectedAcademicYear,
      date_range_from: state.validFrom,
      date_range_to: state.validTo || null,
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (academicYears.length === 0) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Alert severity="warning">
          No academic years found for this school. Please create one in{' '}
          <strong>Settings → Academic Years</strong> first, then come back to continue.
          <br />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Note: Draft, published, and archived years all appear here.
          </Typography>
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          {onBack && <Button onClick={onBack}>Back</Button>}
        </Box>
      </Box>
    );
  }

  const selectedAy = academicYears.find((a) => a.name === state.selectedAcademicYear);

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6">Academic Year for Timetable</Typography>

      <FormControl fullWidth>
        <InputLabel id="academic-year-label">Academic Year</InputLabel>
        <Select
          labelId="academic-year-label"
          label="Academic Year"
          value={state.selectedAcademicYear}
          onChange={(e) => handleYearChange(e.target.value)}
        >
          {academicYears.map((ay) => (
            <MenuItem key={ay.id} value={ay.name}>
              {ay.name}{ay.status !== 'published' ? ` (${ay.status})` : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedAy && (
        <>
          {/* Effective From — must be > today */}
          <TextField
            label="Effective From"
            type="date"
            value={state.validFrom}
            inputProps={{ min: tomorrow() }}
            onChange={(e) =>
              dispatch({ type: 'SET_DATE_RANGE', payload: { validFrom: e.target.value, validTo: state.validTo } })
            }
            InputLabelProps={{ shrink: true }}
            error={!!validFromError}
            helperText={validFromError ?? 'The date from which this timetable version becomes active. Any existing timetable will be closed the day before.'}
            fullWidth
          />

          {/* Valid To — auto-set to academic year end, read-only */}
          <TextField
            label="Valid To (end of academic year)"
            type="date"
            value={state.validTo}
            InputLabelProps={{ shrink: true }}
            InputProps={{ readOnly: true }}
            helperText={`Automatically set to the end of ${selectedAy.name}`}
            fullWidth
            sx={{ '& input': { color: 'text.secondary' } }}
          />
        </>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {onBack && <Button onClick={onBack}>Back</Button>}
        <Button variant="contained" disabled={!canAdvance} onClick={handleNext} sx={{ ml: 'auto' }}>
          Next
        </Button>
      </Box>
    </Box>
  );
}
